import { apiUrl } from '../lib/api';
import { reportClientError } from '../lib/report';
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

/** True when the browser reports no network. Updates live. */
export function useOnline() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

/** Thin banner while offline. Data already loaded stays visible; actions fail with a clear message. */
export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div className="offline-banner" role="status">
      You’re offline. You can keep looking at what’s loaded; changes will work again once you’re connected.
    </div>
  );
}

/**
 * Route-level error screen: a crash on one page keeps the app's navigation working. Never shows error details.
 * After a new deploy, an old tab can fail to load a code chunk; that case reloads once automatically.
 */
export function RouteError() {
  const error = useRouteError();
  const message = error instanceof Error ? error.message : '';
  const staleChunk = /dynamically imported module|Importing a module script failed|Failed to fetch/i.test(message);
  useEffect(() => {
    console.error('Route error', error);
    if (!staleChunk) reportClientError('dashboard', apiUrl, error);
    if (staleChunk && !sessionStorage.getItem('sd_chunk_reload')) {
      sessionStorage.setItem('sd_chunk_reload', '1');
      window.location.reload();
    }
  }, [error, staleChunk]);
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <section className="route-error" role="alert">
      <h1>{notFound ? 'Page not found.' : 'Something went wrong on this page.'}</h1>
      <p>{notFound ? 'The link may be old or mistyped.' : 'Please try again. If it keeps happening, reload the page.'}</p>
      <div className="route-error__actions">
        <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
          Reload
        </button>
        <Link to="/" className="btn btn--secondary">
          Go to start
        </Link>
      </div>
    </section>
  );
}
